import React from "react";
import { connect } from "react-redux";
import { withRouter } from "react-router-dom";
import { withStyles } from "@material-ui/styles";
import update from "immutability-helper";
import { I18n } from "@aws-amplify/core";
import { get, size, includes } from "lodash";
import { validate as uuidValidate } from "uuid";
import withWidth, { isWidthDown } from "@material-ui/core/withWidth";
import Grid from "@material-ui/core/Grid";
import Box from "@material-ui/core/Box";

import { fetchGet } from "../../api/fetch";
import { setBreadcrumb } from "../../actions/common";
import { getUser, isAllowed } from "../../utils/users";
import ProfileMobileMenu from "./ProfileMobileMenu";
import ProfileTabs from "./ProfileTabs";
import ProfileDetails from "./ProfileDetails";
import ProfileImages from "./ProfileImages";
import ProfileEvents from "./ProfileEvents";
import ProfileNotes from "./ProfileNotes";
import ProfileLog from "./ProfileLog";
import ProfileVerification from "./ProfileVerification";
import ProfileTemperature from "./ProfileTemperature";
import ProfileDataPrivacy from "./DataPrivacy/ProfileDataPrivacy";
import Loader from "../Common/Loader";
import {
  details,
  photos,
  events,
  historyLog,
  verification,
  temperature,
  notes,
  dataPrivacy,
  associations,
} from "../../constants/profiles";
import images from "../../assets/img/images";
import colors from "../../assets/style/colors";
import ProfileAssociations from "./ProfileAssociations/ProfileAssociations";
import { getProfileTypeIcon } from "../../utils/profile";

const styles = (theme) => ({
  mainContent: {
    [theme.breakpoints.up("sm")]: {
      height: "100%",
      padding: "12px",
    },
    [theme.breakpoints.down("sm")]: {
      height: "100%",
      padding: "0px",
    },
  },
  loader: {
    width: "100%",
    [theme.breakpoints.down("sm")]: {
      height: "calc(100vh - 96px)",
    },
  },
});

const Profile = (props) => {
  const { classes, dispatch, language } = props;
  const [tabValue, setTabValue] = React.useState(details);
  const [profile, setProfile] = React.useState();
  const [profileAttributes, setProfileAttributes] = React.useState();

  const isMe =
    !get(props, ["match", "params", "id3"]) ||
    get(props, ["match", "params", "id3"]) === "me";
  const section = get(props, ["match", "params", "profileSection"]) || details;
  const profileId = isMe
    ? get(props, ["user", "profile", "id"])
    : get(props, ["match", "params", "id3"]);

  const permissions = get(props.user, ["permissions"]);

  React.useEffect(() => {
    if (
      uuidValidate(profileId) ||
      includes(profileId, "00000000-0000-0000-0000")
    ) {
      fetchProfile();
    } else {
      props.history.push("/profiles/me");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profileId]);

  React.useEffect(() => {
    setTabValue(section);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [section]);

  const fetchProfile = async () => {
    try {
      let profile = await fetchGet(`profiles/${profileId}`);
      let profileType = profile.type.id;
      if (get(profile, ["user"])) {
        try {
          let user = await fetchGet(`users/${get(profile, ["user", "id"])}`);
          profile.user.username = get(user, ["cognito", "username"]);
        } catch (e) {
          console.log(e);
        }
      }

      setProfile(profile);
      dispatch(
        setBreadcrumb(
          [
            { name: I18n.get("topbar_profiles"), url: "/profiles" },
            { name: isMe ? I18n.get("topbar_me") : profile.name },
          ],
          getProfileTypeIcon(profileType)
        )
      );
      let attributes = await fetchGet(
        `profile_types/${profileType}/attributes`
      );
      setProfileAttributes(attributes);
      if (section === "notes") loadNotes(profile);
    } catch (e) {
      console.log(e);
      props.history.push("/profiles/me");
    }
  };

  const changeTab = (newValue) => {
    props.history.push(`/profiles/${isMe ? "me" : profileId}/${newValue}`);
    if (newValue === notes) {
      loadNotes();
    }
  };

  const loadNotes = async (p = profile) => {
    try {
      let notes = [...p.notes];
      let authors = {};
      for (let i = 0; i < notes.length; i++) {
        if (notes[i].updated_by) {
          try {
            let author = await getUser(notes[i].updated_by);
            authors[notes[i].id] =
              author.profile.firstname + " " + author.profile.lastname;
          } catch (e) {
            console.log(e);
            authors[notes[i].id] = "";
          }
        } else {
          authors[notes[i].id] = "";
        }
      }
      notes.forEach((n) => {
        n.author = authors[n.id];
      });
      setProfile(
        update(p, {
          notes: { $set: notes },
        })
      );
    } catch (e) {
      console.log(e);
    }
  };

  const addNewNoteToProfile = (note) => {
    setProfile(
      update(profile, {
        notes: { $push: [note] },
      })
    );
  };

  if (!profile) {
    return (
      <Box className={classes.loader} style={{ height: "100%" }}>
        <Loader height="100%" />
      </Box>
    );
  }

  const mobileDisplay = isWidthDown("sm", props.width);

  function getWidth() {
    return Math.max(
      document.body.scrollWidth,
      document.documentElement.scrollWidth,
      document.body.offsetWidth,
      document.documentElement.offsetWidth,
      document.documentElement.clientWidth
    );
  }

  return (
    <Grid container direction="column" wrap="nowrap">
      {mobileDisplay ? (
        <Grid item style={{ height: "40px", backgroundColor: colors.white }}>
          <Grid container>
            {getWidth() < 478 && (
              <Grid
                item
                style={{
                  textAlign: "center",
                  border: `1px solid ${colors.primary}`,
                  borderLeft: "none",
                  width: "60px",
                  height: "40px",
                }}
              >
                <Grid container direction="column" justify="center">
                  <Grid item style={{ marginTop: "3px" }}>
                    <img src={images.meColored} width="20px" alt="me" />
                  </Grid>
                  <Grid
                    item
                    style={{
                      color: colors.primary,
                      marginTop: "-5px",
                      fontSize: "0.75em",
                    }}
                  >
                    {I18n.get("sidebar_me")}
                  </Grid>
                </Grid>
              </Grid>
            )}

            <Grid
              item
              xs
              className="flexCenter text-87 bold-500"
              style={{ justifyContent: "center" }}
            >
              {I18n.get(`profiles_${section}`)}
            </Grid>
            <Grid item className="flexCenter">
              <ProfileMobileMenu changeMenu={changeTab} />
            </Grid>
          </Grid>
        </Grid>
      ) : (
        <Grid item className="titleBar" style={{ paddingLeft: 0 }}>
          <ProfileTabs
            profile={profile}
            tabValue={tabValue}
            setTabValue={changeTab}
            notesCount={size(get(profile, ["notes"]))}
            isMe={isMe}
          />
        </Grid>
      )}
      <Grid item className={classes.mainContent}>
        {tabValue === details &&
          ((isMe && isAllowed(permissions, "me.details.read")) || !isMe) && (
            <ProfileDetails
              profile={profile}
              refresh={fetchProfile}
              profileAttributes={profileAttributes}
              permissions={permissions}
              isMe={isMe}
            />
          )}
        {tabValue === photos &&
          ((isMe && isAllowed(permissions, "me.photos.read")) || !isMe) && (
            <ProfileImages
              profileId={profileId}
              profileType={profile.profile_type_id}
              refreshProfile={fetchProfile}
              permissions={permissions}
              isMe={isMe}
            />
          )}
        {tabValue === events &&
          ((isMe && isAllowed(permissions, "me.events.read")) || !isMe) && (
            <ProfileEvents profileId={profileId} language={language} />
          )}
        {tabValue === historyLog &&
          ((isMe && isAllowed(permissions, "me.historylog.read")) || !isMe) && (
            <ProfileLog
              profile={profile}
              language={language}
              permissions={permissions}
              isMe={isMe}
            />
          )}
        {tabValue === verification &&
          ((isMe && isAllowed(permissions, "me.verification.read")) ||
            !isMe) && (
            <ProfileVerification
              profile={profile}
              language={language}
              permissions={permissions}
              isMe={isMe}
            />
          )}
        {tabValue === temperature &&
          get(profile, ["type", "id"]) === 1 &&
          ((isMe && isAllowed(permissions, "me.temperature.read")) ||
            !isMe) && (
            <ProfileTemperature
              profileId={get(profile, ["id"])}
              language={language}
            />
          )}
        {tabValue === associations &&
          isAllowed(permissions, "profile_associations.read") && (
            <ProfileAssociations profile={profile} permissions={permissions} />
          )}
        {tabValue === notes &&
          ((isMe && isAllowed(permissions, "me.notes.read")) || !isMe) && (
            <ProfileNotes
              notes={profile.notes}
              profileId={profile.id}
              addNewNoteToProfile={addNewNoteToProfile}
              permissions={permissions}
              isMe={isMe}
            />
          )}
        {tabValue === dataPrivacy &&
          isMe &&
          ((isMe && isAllowed(permissions, "me.dataprivacy.read")) ||
            !isMe) && (
            <ProfileDataPrivacy
              profile={profile}
              refresh={fetchProfile}
              profileAttributes={profileAttributes}
              permissions={permissions}
              isMe={isMe}
            />
          )}
      </Grid>
    </Grid>
  );
};

const mapStateToProps = (state) => {
  return {
    user: state.user,
    language: state.user.language,
  };
};

export default withRouter(
  connect(mapStateToProps)(withWidth()(withStyles(styles)(Profile)))
);
